import type { components } from "../../api/generated-schema";
import {
  ApiContractError,
  ApiProblem,
  apiRequest,
  apiRequestWithMeta,
  createRequestId,
} from "../../api/httpClient";
import type { ProblemDetails } from "../../api/httpClient";
import { getAuthenticationSession } from "../../auth/session";

/**
 * API module typed cho nghĩa trang (SCR-032/SCR-033 — S11.6, khuôn `nccProfileApi`
 * + `organizationAdminApi`). Type lấy từ {@code generated-schema}; UI chỉ render
 * field đã được contract cho phép, không tự suy diễn hay mask.
 */
export type CemeteryListRow = components["schemas"]["CemeteryListRow"];
export type CemeteryPageData = components["schemas"]["CemeteryPageData"];
export type CemeteryPageMeta = components["schemas"]["PageMeta"];

/** Trang kết quả search: `rows` từ `data` + `meta` phân trang từ envelope. */
export type CemeterySearchPage = Readonly<{
  rows: CemeteryPageData["rows"];
  meta: CemeteryPageMeta | null;
}>;
export type CemeteryData = components["schemas"]["CemeteryData"];
/** Số liệu động của hồ sơ (đếm mộ theo danh tính/tình trạng vật lý). */
export type CemeteryGraveStats = components["schemas"]["CemeteryGraveStats"];
/** Nút cây khu/lô/hàng (mảng phẳng, dựng cây theo parentSectionId). */
export type CemeterySectionNode = components["schemas"]["CemeterySectionNode"];
export type CemeteryCommandData = components["schemas"]["CemeteryCommandData"];
export type CemeteryManagementHistoryEntryData =
  components["schemas"]["CemeteryManagementHistoryEntryData"];
export type CemeteryManagementHistoryPageData =
  components["schemas"]["CemeteryManagementHistoryPageData"];
export type CreateCemeteryInput = components["schemas"]["CreateCemeteryRequest"];
export type UpdateCemeteryInput = components["schemas"]["UpdateCemeteryRequest"];

/** Bộ lọc searchCemeteries — đúng whitelist transport của contract. */
export type CemeterySearchFilters = Readonly<{
  page?: number;
  size?: number;
  q?: string;
  status?: string;
  adminAreaId?: string;
  managingOrganizationId?: string;
}>;

/** Bộ lọc export — cùng predicate, không phân trang (rowLimit do server). */
export type CemeteryExportFilters = Omit<CemeterySearchFilters, "page" | "size">;

/** Phân trang lịch sử quản lý (append-only, mới nhất trước). */
export type CemeteryHistoryPageQuery = Readonly<{
  page?: number;
  size?: number;
}>;

const BASE_PATH = "/cemeteries";

function signalInit(signal?: AbortSignal): RequestInit {
  return signal ? { signal } : {};
}

function commandHeaders(idempotencyKey: string): Record<string, string> {
  return { "Idempotency-Key": idempotencyKey, "X-Request-Id": createRequestId() };
}

function encodeQuery(entries: Array<[string, string]>): string {
  const query = new URLSearchParams();
  for (const [key, value] of entries) query.set(key, value);
  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
}

function pageEntries(page: number | undefined, size: number | undefined): Array<[string, string]> {
  const entries: Array<[string, string]> = [];
  if (page !== undefined) entries.push(["page", String(page)]);
  if (size !== undefined) entries.push(["size", String(size)]);
  return entries;
}

function filterEntries(filters: CemeterySearchFilters): Array<[string, string]> {
  const entries = pageEntries(filters.page, filters.size);
  const pushText = (key: string, value: string | undefined): void => {
    if (value !== undefined && value !== "") entries.push([key, value]);
  };
  pushText("q", filters.q);
  pushText("status", filters.status);
  pushText("adminAreaId", filters.adminAreaId);
  pushText("managingOrganizationId", filters.managingOrganizationId);
  return entries;
}

function postCommand<T>(
  path: string,
  body: unknown,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    headers: commandHeaders(idempotencyKey),
    body: JSON.stringify(body),
    ...signalInit(signal),
  });
}

function patchCommand<T>(
  path: string,
  body: unknown,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, {
    method: "PATCH",
    headers: commandHeaders(idempotencyKey),
    body: JSON.stringify(body),
    ...signalInit(signal),
  });
}

async function toApiProblem(response: Response): Promise<ApiProblem | ApiContractError> {
  const headerRequestId = response.headers.get("X-Request-Id") ?? undefined;
  try {
    const body = (await response.json()) as ProblemDetails;
    if (body && typeof body.code === "string") {
      return new ApiProblem(response.status, body);
    }
  } catch {
    // fall through — body không phải problem+json
  }
  return new ApiContractError(
    `unexpected export response (HTTP ${response.status})`,
    response.status,
    headerRequestId,
  );
}

/**
 * {@code GET /cemeteries} — trang nghĩa trang theo phạm vi dữ liệu người gọi
 * (operationId {@code searchCemeteries}).
 */
export async function searchCemeteries(
  filters: CemeterySearchFilters,
  signal?: AbortSignal,
): Promise<CemeterySearchPage> {
  const page = await apiRequestWithMeta<CemeteryPageData, CemeteryPageMeta>(
    `${BASE_PATH}${encodeQuery(filterEntries(filters))}`,
    signalInit(signal),
  );
  return { rows: page.data.rows, meta: page.meta };
}

/**
 * {@code GET /cemeteries/export} — quyền riêng EXPORT_EXECUTE + audit server-side.
 * `apiRequest` chỉ parse envelope JSON nên đường CSV dùng fetch trực tiếp với cùng
 * header xác thực; lỗi chuẩn hóa thành {@code ApiProblem}.
 */
export async function exportCemeteries(
  filters: CemeteryExportFilters,
  signal?: AbortSignal,
): Promise<Blob> {
  const path = `${BASE_PATH}/export${encodeQuery(filterEntries(filters))}`;
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
  const authentication = getAuthenticationSession();
  const headers = new Headers({
    Accept: "text/csv, application/problem+json",
    Authorization: `Bearer ${await authentication.getAccessToken()}`,
    "X-Request-Id": createRequestId(),
  });
  const response = await fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers,
    ...signalInit(signal),
  });
  if (!response.ok) {
    throw await toApiProblem(response);
  }
  return response.blob();
}

/**
 * {@code GET /cemeteries/{cemeteryId}} — projection chi tiết kèm cây khu/lô/hàng và
 * số liệu động (operationId {@code getCemetery}).
 */
export function getCemetery(cemeteryId: string, signal?: AbortSignal): Promise<CemeteryData> {
  return apiRequest<CemeteryData>(
    `${BASE_PATH}/${encodeURIComponent(cemeteryId)}`,
    signalInit(signal),
  );
}

/**
 * {@code GET /cemeteries/{cemeteryId}/management-history} — lịch sử thay đổi đơn vị
 * quản lý/địa bàn, append-only và mới nhất trước (operationId
 * {@code getCemeteryManagementHistory}).
 */
export function getCemeteryManagementHistory(
  cemeteryId: string,
  page: CemeteryHistoryPageQuery,
  signal?: AbortSignal,
): Promise<CemeteryManagementHistoryPageData> {
  return apiRequest<CemeteryManagementHistoryPageData>(
    `${BASE_PATH}/${encodeURIComponent(cemeteryId)}/management-history${encodeQuery(
      pageEntries(page.page, page.size),
    )}`,
    signalInit(signal),
  );
}

/**
 * {@code POST /cemeteries} — tạo nghĩa trang kèm dòng quản lý hiệu lực mở; lệnh ghi
 * bắt buộc Idempotency-Key.
 */
export function createCemetery(
  input: CreateCemeteryInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<CemeteryCommandData> {
  return postCommand<CemeteryCommandData>(BASE_PATH, input, idempotencyKey, signal);
}

/**
 * {@code PATCH /cemeteries/{cemeteryId}} — cập nhật nghĩa trang; đổi quản lý ghi thêm
 * dòng lịch sử hiệu lực.
 */
export function updateCemetery(
  cemeteryId: string,
  input: UpdateCemeteryInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<CemeteryCommandData> {
  return patchCommand<CemeteryCommandData>(
    `${BASE_PATH}/${encodeURIComponent(cemeteryId)}`,
    input,
    idempotencyKey,
    signal,
  );
}
